import Phaser from 'phaser';
import { db, type Lesson } from '../../data/db';
import { appState } from '../../app/state';
import { COLORS, drawWizard, drawParallaxBg } from '../util/draw';
import { isLessonMastered } from '../../learning/srs';

interface PortalView {
  lesson: Lesson;
  x: number;
  unlocked: boolean;
  mastered: boolean;
}

export class AcademyScene extends Phaser.Scene {
  constructor() { super({ key: 'Academy' }); }

  async create() {
    const { width, height } = this.scale;
    drawParallaxBg(this, COLORS.bg);

    const title = this.add.text(24, 20, 'Wizard Academy', {
      color: '#f5efff', fontSize: '24px', fontFamily: '-apple-system, sans-serif', fontStyle: 'bold',
    });
    const profile = appState.get().profile;
    if (profile) {
      this.add.text(24, 50, `${profile.avatar}  ${profile.name}`, {
        color: '#f5efff', fontSize: '14px', fontFamily: '-apple-system, sans-serif',
      }).setAlpha(0.85);
    }

    // Top-right buttons: Parent, Switch profile
    this.makeButton(width - 130, 36, 'Parent View', () => appState.goto({ kind: 'parent' }));
    this.makeButton(width - 130, 76, 'Switch wizard', () => appState.goto({ kind: 'profile-picker' }));

    // Hero in the middle of the academy
    const heroG = this.add.graphics();
    const heroY = height * 0.72;
    drawWizard(heroG, width / 2, heroY, 1.1);

    // Welcome text
    this.add.text(width / 2, heroY - 110, 'Choose a quest', {
      color: '#f5efff', fontSize: '18px', fontFamily: '-apple-system, sans-serif',
    }).setOrigin(0.5).setAlpha(0.9);

    // Load lessons → render zone portals.
    const lessons = await db.lessons.orderBy('createdAt').toArray();
    const progress = appState.get().progress;
    const portals: PortalView[] = [];

    // Determine unlock state: first lesson always unlocked; subsequent gated by
    // isLessonMastered() of the previous in display order. Already-unlocked
    // zones from progress remain unlocked.
    const unlockedSet = new Set(progress?.unlockedZoneIds ?? []);
    let canUnlockNext = true;
    for (let i = 0; i < lessons.length; i++) {
      const l = lessons[i];
      const isFirst = i === 0;
      const explicitlyUnlocked = unlockedSet.has(l.id);
      const unlocked = isFirst || explicitlyUnlocked || canUnlockNext;
      const mastered = await isLessonMastered(profile!.id, l.id).catch(() => false);
      canUnlockNext = canUnlockNext && mastered;
      portals.push({ lesson: l, x: 0, unlocked, mastered });
    }

    // Layout: row of cards
    const cardW = 200;
    const gap = 16;
    const totalW = portals.length * cardW + (portals.length - 1) * gap;
    const startX = Math.max(20, (width - totalW) / 2);
    for (let i = 0; i < portals.length; i++) {
      portals[i].x = startX + i * (cardW + gap);
      this.drawPortal(portals[i], 140, cardW);
    }

    // Footer hint
    this.add.text(width / 2, height - 24, 'Add new zones from Parent View → Add workbook page', {
      color: '#f5efff', fontSize: '12px', fontFamily: '-apple-system, sans-serif',
    }).setOrigin(0.5).setAlpha(0.5);
  }

  private drawPortal(p: PortalView, y: number, cardW: number) {
    const g = this.add.graphics();
    g.fillStyle(p.unlocked ? COLORS.panel : 0x1a1530, 1);
    g.lineStyle(2, p.mastered ? COLORS.gold : COLORS.accent2, 1);
    g.fillRoundedRect(p.x, y, cardW, 150, 14);
    g.strokeRoundedRect(p.x, y, cardW, 150, 14);

    // Theme glyph
    const glyph = themeGlyph(p.lesson.zoneTheme);
    this.add.text(p.x + cardW / 2, y + 50, glyph, {
      fontSize: '48px',
    }).setOrigin(0.5).setAlpha(p.unlocked ? 1 : 0.3);

    this.add.text(p.x + cardW / 2, y + 100, p.lesson.title, {
      color: '#f5efff', fontSize: '14px', fontFamily: '-apple-system, sans-serif',
      align: 'center',
      wordWrap: { width: cardW - 20 },
    }).setOrigin(0.5).setAlpha(p.unlocked ? 1 : 0.4);

    const status = p.mastered ? '★ mastered' : p.unlocked ? 'enter →' : '🔒 locked';
    this.add.text(p.x + cardW / 2, y + 128, status, {
      color: p.mastered ? '#ffe27a' : p.unlocked ? '#4dd672' : '#9b8db5',
      fontSize: '12px',
      fontFamily: '-apple-system, sans-serif',
      fontStyle: p.mastered ? 'bold' : 'normal',
    }).setOrigin(0.5);

    if (p.unlocked) {
      const hit = this.add.rectangle(p.x + cardW / 2, y + 75, cardW, 150, 0x000000, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => appState.goto({ kind: 'zone', lessonId: p.lesson.id }));
    }
  }

  private makeButton(x: number, y: number, label: string, onClick: () => void) {
    const w = 130, h = 32;
    const g = this.add.graphics();
    g.fillStyle(COLORS.panel, 1);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 10);
    const t = this.add.text(x, y, label, {
      color: '#f5efff', fontSize: '13px', fontFamily: '-apple-system, sans-serif',
    }).setOrigin(0.5);
    const hit = this.add.rectangle(x, y, w, h, 0x000000, 0).setInteractive({ useHandCursor: true });
    hit.on('pointerdown', onClick);
    return { g, t, hit };
  }
}

function themeGlyph(theme: string): string {
  switch (theme) {
    case 'animals': return '🐯';
    case 'numbers': return '🔢';
    case 'colors': return '🌈';
    case 'family': return '👨‍👩‍👧';
    case 'food': return '🍎';
    case 'weather': return '⛅️';
    case 'nature': return '🌳';
    case 'school': return '📚';
    case 'body': return '🖐';
    case 'actions': return '🏃';
    case 'emotions': return '😀';
    default: return '✨';
  }
}
